import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getComplaintById, downloadFile } from "../../services/complaintService";
import html2pdf from "html2pdf.js";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

export default function AdminComplaintView() {
  const { id } = useParams();
  const [complaint, setComplaint] = useState(null);
  const [filePreviewMap, setFilePreviewMap] = useState({});
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const complaintPdfRef = useRef(null);
  const normalizeFileName = (value = "") =>
    value
      .trim()
      .replace(/^['"]+|['"]+$/g, "")
      .replace(/^\[+|\]+$/g, "");

  const areSameFile = (a = "", b = "") =>
    normalizeFileName(a).toLowerCase() === normalizeFileName(b).toLowerCase();

  useEffect(() => {
    fetchComplaint();
  }, []);

  const fetchComplaint = async () => {
    try {
      const data = await getComplaintById(id);
      setComplaint(data);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch complaint details.");
    }
  };

  useEffect(() => {
    let isCancelled = false;
    const createdUrls = [];

    const buildPreviews = async () => {
      if (!complaint) return;

      const attachmentNames = [];
      const parsedFiles = complaint.filePath
        ? complaint.filePath.split(",").map((item) => normalizeFileName(item)).filter(Boolean)
        : [];
      attachmentNames.push(...parsedFiles);

      const aadhaarCandidate =
        complaint.aadharCardFile ||
        complaint.AadharCardFile ||
        complaint.aadharCardFilePath ||
        complaint.AadharCardFilePath ||
        complaint.adharCardFile ||
        complaint.AdharCardFile ||
        complaint.adharCardFilePath ||
        complaint.AdharCardFilePath ||
        "";
      const cleanedAadhaarCandidate = aadhaarCandidate ? normalizeFileName(aadhaarCandidate) : "";

      if (cleanedAadhaarCandidate) {
        attachmentNames.push(cleanedAadhaarCandidate);
      }

      const uniqueNames = [...new Set(attachmentNames)];
      const nextMap = {};

      for (const name of uniqueNames) {
        try {
          const blob = await downloadFile(name);
          const blobType = (blob?.type || "").toLowerCase();
          const ext = name.split(".").pop()?.toLowerCase() || "";
          const isImage =
            blobType.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
          const isPdf = blobType.includes("pdf") || ext === "pdf";
          const objectUrl = URL.createObjectURL(blob);
          createdUrls.push(objectUrl);
          nextMap[name] = { url: objectUrl, isImage, isPdf };
        } catch (err) {
          console.error(`Failed to load preview for file: ${name}`, err);
          nextMap[name] = { url: "", isImage: false, isPdf: false };
        }
      }

      if (!isCancelled) {
        setFilePreviewMap(nextMap);
      }
    };

    buildPreviews();

    return () => {
      isCancelled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [complaint]);

  const handleDownload = async (fileName) => {
    try {
      const blob = await downloadFile(fileName);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      alert("Failed to download file.");
    }
  };

  const getAttachmentNames = () => {
    if (!complaint) return [];
    const fileNames = complaint.filePath
      ? complaint.filePath.split(",").map((item) => normalizeFileName(item)).filter(Boolean)
      : [];
    const aadhaarCandidate =
      complaint.aadharCardFile ||
      complaint.AadharCardFile ||
      complaint.aadharCardFilePath ||
      complaint.AadharCardFilePath ||
      complaint.adharCardFile ||
      complaint.AdharCardFile ||
      complaint.adharCardFilePath ||
      complaint.AdharCardFilePath ||
      "";
    const cleanedAadhaar = aadhaarCandidate ? normalizeFileName(aadhaarCandidate) : "";
    const allFiles = cleanedAadhaar ? [...fileNames, cleanedAadhaar] : fileNames;
    const unique = [];
    const seen = new Set();
    allFiles.forEach((file) => {
      const key = normalizeFileName(file).toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(file);
    });
    return unique;
  };

  const handleDownloadPdf = async () => {
    if (!complaintPdfRef.current || !complaint) return;
    setIsGeneratingPdf(true);

    try {
      const fileName = `Complaint_${complaint.applicationUniqueID || complaint.complaintID || id}.pdf`;
      const options = {
        margin: [0.35, 0.4, 0.35, 0.4],
        filename: fileName,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 2.2,
          useCORS: true,
          backgroundColor: "#ffffff",
          ignoreElements: (el) => el.classList?.contains("pdf-exclude"),
        },
        pagebreak: { mode: ["css", "legacy"], avoid: [".avoid-page-break"] },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait", compress: true },
      };

      const reportWorker = html2pdf().set(options).from(complaintPdfRef.current).toPdf();
      const reportArrayBuffer = await reportWorker.outputPdf("arraybuffer");

      const finalPdf = await PDFDocument.create();
      const fontRegular = await finalPdf.embedFont(StandardFonts.Helvetica);
      const fontBold = await finalPdf.embedFont(StandardFonts.HelveticaBold);
      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;
      const generatedStamp = new Date().toLocaleString();

      const appendPdfBytes = async (pdfBytes) => {
        const source = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
        const copiedPages = await finalPdf.copyPages(source, source.getPageIndices());
        copiedPages.forEach((page) => finalPdf.addPage(page));
      };

      const addImagePage = async (imageBytes, isPng) => {
        const page = finalPdf.addPage([A4_WIDTH, A4_HEIGHT]);
        const image = isPng ? await finalPdf.embedPng(imageBytes) : await finalPdf.embedJpg(imageBytes);
        const imageDims = image.scale(1);
        const maxWidth = A4_WIDTH - 80;
        const maxHeight = A4_HEIGHT - 140;
        const scale = Math.min(maxWidth / imageDims.width, maxHeight / imageDims.height, 1);
        const width = imageDims.width * scale;
        const height = imageDims.height * scale;
        const x = (A4_WIDTH - width) / 2;
        const y = (A4_HEIGHT - height) / 2 - 20;

        page.drawImage(image, { x, y, width, height });
      };

      await appendPdfBytes(reportArrayBuffer);

      const attachmentNames = getAttachmentNames();
      for (const attachmentName of attachmentNames) {
        try {
          const blob = await downloadFile(attachmentName);
          const type = (blob?.type || "").toLowerCase();
          const ext = attachmentName.split(".").pop()?.toLowerCase() || "";
          const bytes = await blob.arrayBuffer();
          const header = new Uint8Array(bytes.slice(0, 5));
          const pdfMagic =
            header.length >= 5 &&
            header[0] === 0x25 &&
            header[1] === 0x50 &&
            header[2] === 0x44 &&
            header[3] === 0x46 &&
            header[4] === 0x2d; // %PDF-
          const pngMagic =
            header.length >= 4 &&
            header[0] === 0x89 &&
            header[1] === 0x50 &&
            header[2] === 0x4e &&
            header[3] === 0x47;

          const isPdf = type.includes("pdf") || ext === "pdf" || pdfMagic;
          const isPng = type.includes("png") || ext === "png" || pngMagic;
          const isImage = type.startsWith("image/") || ["jpg", "jpeg", "png"].includes(ext) || pngMagic;

          if (isPdf) {
            await appendPdfBytes(bytes);
          } else if (isImage) {
            await addImagePage(bytes, isPng);
          } else {
            const page = finalPdf.addPage([A4_WIDTH, A4_HEIGHT]);
            page.drawText("Unsupported attachment type", {
              x: 40,
              y: A4_HEIGHT - 60,
              size: 14,
              font: fontBold,
              color: rgb(0.75, 0.1, 0.1),
            });
          }
        } catch (err) {
          console.error(`Failed to append attachment in PDF: ${attachmentName}`, err);
        }
      }

      const addFooterToAllPages = () => {
        const pages = finalPdf.getPages();
        const totalPages = pages.length;
        const footerY = 22;
        const leftText = "BSCPCR - Complaint Management";
        const centerText = `Generated: ${generatedStamp}`;

        pages.forEach((page, index) => {
          const { width } = page.getSize();
          page.drawLine({
            start: { x: 26, y: footerY + 12 },
            end: { x: width - 26, y: footerY + 12 },
            thickness: 0.6,
            color: rgb(0.82, 0.84, 0.87),
          });

          page.drawText(leftText, {
            x: 26,
            y: footerY,
            size: 8.5,
            font: fontRegular,
            color: rgb(0.33, 0.37, 0.42),
          });

          const centerWidth = fontRegular.widthOfTextAtSize(centerText, 8.5);
          page.drawText(centerText, {
            x: (width - centerWidth) / 2,
            y: footerY,
            size: 8.5,
            font: fontRegular,
            color: rgb(0.33, 0.37, 0.42),
          });

          const pageText = `Page ${index + 1} of ${totalPages}`;
          const pageTextWidth = fontRegular.widthOfTextAtSize(pageText, 8.5);
          page.drawText(pageText, {
            x: width - 26 - pageTextWidth,
            y: footerY,
            size: 8.5,
            font: fontRegular,
            color: rgb(0.33, 0.37, 0.42),
          });
        });
      };

      addFooterToAllPages();

      const finalBytes = await finalPdf.save();
      const blob = new Blob([finalBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to generate merged PDF with attachments.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (!complaint) return <div className="text-center mt-5">Loading...</div>;

  const files = complaint.filePath
    ? complaint.filePath.split(",").map((item) => normalizeFileName(item)).filter(Boolean)
    : [];
  const aadhaarNumber =
    complaint.aadharNumber ||
    complaint.AadharNumber ||
    complaint.adharNumber ||
    complaint.AdharNumber ||
    "N/A";
  const aadhaarFile =
    complaint.aadharCardFile ||
    complaint.AadharCardFile ||
    complaint.aadharCardFilePath ||
    complaint.AadharCardFilePath ||
    complaint.adharCardFile ||
    complaint.AdharCardFile ||
    complaint.adharCardFilePath ||
    complaint.AdharCardFilePath ||
    "";
  const cleanedAadhaarFile = aadhaarFile ? normalizeFileName(aadhaarFile) : "";
  const displayFiles = files.filter((file) => !cleanedAadhaarFile || !areSameFile(file, cleanedAadhaarFile));
  const aadhaarPreview = filePreviewMap[cleanedAadhaarFile] || filePreviewMap[aadhaarFile];
  const remarks = complaint.remarks || complaint.reason;
  const socialMediaLinks = complaint.socialMediaLink
    ? complaint.socialMediaLink.split(",").map((item) => item.trim()).filter(Boolean)
    : [];
  const appliedDateRaw = complaint.appliedDate || complaint.AppliedDate || complaint.createdDate || complaint.CreatedDate || complaint.createdAt;
  const appliedDate = appliedDateRaw && !Number.isNaN(new Date(appliedDateRaw).getTime())
    ? new Date(appliedDateRaw).toLocaleString()
    : "N/A";
  const statusChangedDateRaw = complaint.statusChangedDate || complaint.StatusChangedDate;
  const statusChangedDate = statusChangedDateRaw && !Number.isNaN(new Date(statusChangedDateRaw).getTime())
    ? new Date(statusChangedDateRaw).toLocaleString()
    : "N/A";
  const generatedOn = new Date().toLocaleString();

  return (
    <div className="container my-5">
      <div
        ref={complaintPdfRef}
        style={{
          background: "#fff",
          color: "#1f2937",
          padding: "8px",
          borderRadius: "10px",
        }}
      >
      <div
        className="p-4 p-md-5 mb-4 rounded-4 text-white avoid-page-break"
        style={{ background: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 60%, #084298 100%)" }}
      >
        <h2 className="mb-2">Complaint Details Online</h2>
        <div className="small mb-3">Generated On: {generatedOn}</div>
        <div className="d-flex flex-wrap gap-2">
          <span className="badge bg-light text-dark fs-6">
            Complaint ID: {complaint.complaintID || complaint.complaintId || id}
          </span>
          <span className="badge bg-light text-dark fs-6">
            Application ID: {complaint.applicationUniqueID || "N/A"}
          </span>
          <span className="badge bg-warning text-dark fs-6">
            Status: {complaint.status || "N/A"}
          </span>
        </div>
      </div>

      <div className="card shadow border-0 p-4 mb-4 rounded-4 avoid-page-break">
        <h5 className="card-title mb-3">Personal Information</h5>
        <div className="row">
          <div className="col-md-6 mb-2"><strong>Complainant Name:</strong> {complaint.complainantName}</div>
          <div className="col-md-6 mb-2"><strong>Email:</strong> {complaint.email}</div>
          <div className="col-md-6 mb-2"><strong>Mobile:</strong> {complaint.mobile}</div>
          <div className="col-md-6 mb-2"><strong>Victim Gender:</strong> {complaint.victimGender}</div>
          <div className="col-md-6 mb-2"><strong>Applied Date:</strong> {appliedDate}</div>
          <div className="col-md-6 mb-2"><strong>Victim Age:</strong> {complaint.victimAge}</div>
          <div className="col-md-6 mb-2"><strong>Relation With Victim:</strong> {complaint.relationWithVictim}</div>
        </div>
      </div>

      <div className="card shadow border-0 p-4 mb-4 rounded-4 avoid-page-break">
        <h5 className="card-title mb-3">Location Details</h5>
        <div className="row">
          <div className="col-md-6 mb-2"><strong>State:</strong> {complaint.state}</div>
          <div className="col-md-6 mb-2"><strong>District:</strong> {complaint.district}</div>
          <div className="col-12 mb-2"><strong>Address:</strong> {complaint.address}</div>
        </div>
      </div>

      <div className="card shadow border-0 p-4 mb-4 rounded-4 avoid-page-break">
        <h5 className="card-title mb-3">Complaint Info</h5>
        <div className="mb-2"><strong>Complaint Head:</strong> {complaint.complaintHead}</div>
        <div className="mb-2"><strong>Complaint Details:</strong> {complaint.complaintDetails}</div>
        <div className="mb-2"><strong>Aadhaar Number:</strong> {aadhaarNumber}</div>
        <div className="mb-2"><strong>Remarks:</strong> {remarks || "N/A"}</div>
        <div className="mb-2">
          <strong>Social Media Link:</strong>{" "}
          {socialMediaLinks.length > 0 ? (
            socialMediaLinks.map((link, index) => (
              <span key={index}>
                  <a href={link} target="_blank" rel="noreferrer" style={{ color: "#0d6efd", textDecoration: "none" }}>
                    {link}
                  </a>
                {index < socialMediaLinks.length - 1 ? ", " : ""}
              </span>
            ))
          ) : (
            "N/A"
          )}
        </div>
        <div className="row">
          <div className="col-md-4 mb-2">
            <strong>Status:</strong>{" "}
            <span className={`badge ${complaint.status === "Completed" ? "bg-success" : "bg-info text-dark"}`}>
              {complaint.status}
            </span>
          </div>
          <div className="col-md-8 mb-2">
            <strong>Status Change Date:</strong> {statusChangedDate}
          </div>
        </div>
      </div>

      <div className="card shadow border-0 p-4 mb-4 rounded-4 avoid-page-break">
        <h5 className="card-title mb-3">Uploaded Files</h5>
        {displayFiles.length > 0 ? (
          <div>
            {displayFiles.map((f, idx) => (
              <div key={idx} className="mb-3">
                <button
                  className="btn btn-outline-primary me-2 mb-2 pdf-exclude"
                  onClick={() => handleDownload(f)}
                >
                  {f}
                </button>
              
              </div>
            ))}
          </div>
        ) : (
          <p>No files uploaded</p>
        )}
      </div>
      </div>

      <div className="text-center d-flex justify-content-center gap-2">
        <button onClick={handleDownloadPdf} className="btn btn-primary" disabled={isGeneratingPdf}>
          {isGeneratingPdf ? "Generating PDF..." : "Download PDF"}
        </button>
        <Link to="/admin/complaints" className="btn btn-secondary">
          Back to Complaints
        </Link>
      </div>
    </div>
  );
}
